import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useFinance } from "./FinanceContext";
import { sumTo } from "./data/model";
import { MONTHS, SEED } from "./data/seedData";
import { AnnualBar } from "./components/AnnualBar";
import { KpiGrid, type KpiItem } from "./components/KpiGrid";
import { StatusBadge } from "./components/StatusChip";
import { toneColor } from "./components/tone";

export function AnnualSummaryPage() {
  const { model, fmt, selMonth, setPage } = useFinance();
  const totRevYTD = model.revenue.reduce((s, r) => s + sumTo(r.actual, selMonth), 0);
  const totApproved = model.items.reduce((s, i) => s + i.approved, 0);
  const totActual = model.items.reduce((s, i) => s + sumTo(i.actual, selMonth), 0);
  const util = totApproved ? (totActual / totApproved) * 100 : 0;
  const profit = totRevYTD - totActual;

  const kpis: KpiItem[] = [
    { label: "Annual Approved Budget", value: fmt(totApproved) },
    { label: "Annual Actual (YTD)", value: fmt(totActual) },
    { label: "Remaining", value: fmt(totApproved - totActual) },
    {
      label: "Budget Utilization",
      value: util.toFixed(1) + "%",
      tone: util > 100 ? "r" : util > 90 ? "a" : "g",
      showBadge: true,
    },
    { label: "Revenue (YTD)", value: fmt(totRevYTD) },
    { label: "Profit / (Loss)", value: fmt(profit), tone: profit >= 0 ? "pos" : "neg" },
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Annual Budget Summary
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Full financial-year executive overview · {SEED.fiscalYear} · actuals YTD through{" "}
        {MONTHS[selMonth]}
      </Typography>
      <AnnualBar />
      <KpiGrid items={kpis} />
      <TableContainer sx={{ maxHeight: "65vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Cost Head</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Annual Budget</TableCell>
              <TableCell align="right">Annual Actual (YTD)</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell align="right">Utilization %</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SEED.categories.map((cat) => {
              const its = model.items.filter((i) => i.catLetter === cat.letter);
              const b = its.reduce((s, i) => s + i.approved, 0);
              const a = its.reduce((s, i) => s + sumTo(i.actual, selMonth), 0);
              const u = b ? (a / b) * 100 : 0;
              const st = !b ? "g" : u > 100 ? "r" : u > 90 ? "a" : "g";
              return (
                <TableRow key={cat.letter}>
                  <TableCell>{cat.code}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{cat.name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={cat.type} />
                  </TableCell>
                  <TableCell align="right">{fmt(b)}</TableCell>
                  <TableCell align="right">{fmt(a)}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(b - a >= 0 ? "pos" : "neg") }}>
                    {fmt(b - a)}
                  </TableCell>
                  <TableCell align="right">{u.toFixed(1)}%</TableCell>
                  <TableCell align="right">
                    <StatusBadge status={st} />
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
              <TableCell colSpan={2}>GRAND TOTAL</TableCell>
              <TableCell />
              <TableCell align="right">{fmt(totApproved)}</TableCell>
              <TableCell align="right">{fmt(totActual)}</TableCell>
              <TableCell
                align="right"
                sx={{ color: toneColor(totApproved - totActual >= 0 ? "pos" : "neg") }}
              >
                {fmt(totApproved - totActual)}
              </TableCell>
              <TableCell align="right">{util.toFixed(1)}%</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1, cursor: "pointer" }}
        onClick={() => setPage("budget")}
      >
        Annual Budget = approved (frozen) annual figure · Revenue &amp; Profit are company-wide
        (shown above) · Open Budget Planning →
      </Typography>
    </Box>
  );
}
